<template>
  <div class="!m-0 divider pt-3"><hr class="!m-0" /></div>
  <div class="scroll-container pt-2">
    <div class="tile-grid-photos gap-1 mt-3">
      <InspirationTiles
        :selected-image-state="props.selectedImageState"
        :templates="currentForegroundInspirations()"
        :is-loading="isLoading"
        :image-button-size="buttonSize"
      />
    </div>
    <div class="pb-3"></div>
  </div>
</template>

<script lang="ts" setup>
import Client from "@/modules/internal_api/client";
import { MagicBackgroundForegroundType, MagicBackgroundTemplate } from "@/modules/internal_api/magic_backgrounds";
import { withExponentialBackoff } from "@/modules/utils";
import { useEditorStore } from "@/stores/editor_store";
import { PersistentStore } from "@/stores/persistent_store";
import { computed, onMounted, ref } from "vue";
import InspirationTiles from "@/components/prism/inspiration_tiles.vue";
import { useMonitorSize } from "@/composables/monitor_size";

export interface InspirationGalleryProps {
  selectedImageState: PersistentStore;
  foregroundType: string;
}

const props = defineProps<InspirationGalleryProps>();
const store = useEditorStore();

const isLoading = ref(false);
const inspirations = computed(() => store.magicBackgrounds.inspirations);

const { isMobile } = useMonitorSize();

const buttonSize = computed(() => (isMobile.value ? "default" : "lg"));

onMounted(async () => {
  const allInspirations = await loadInspirations();
  store.loadInspirations(allInspirations);
});

const loadInspirations = async () => {
  try {
    isLoading.value = true;
    const templatesByType = await withExponentialBackoff(
      () => Client.fetchMagicBackgroundsTemplates({ foregroundType: props.foregroundType }),
      {
        on: [Error],
        retryLimit: 1,
      }
    );
    return templatesByType;
  } catch (err) {
    console.log(err);
  } finally {
    isLoading.value = false;
  }
};

const inspirationPriorityMap = {
  product: ["product", "person", "animal", "car"],
  person: ["person", "animal", "product", "car"],
  car: ["car", "animal", "person", "product"],
  animal: ["animal", "person", "product", "car"],
  graphics: ["animal", "product", "person", "car"],
};

const currentForegroundInspirations = () => {
  if (!inspirations.value) return [];

  const currentType = props.foregroundType.toLowerCase() as keyof typeof inspirationPriorityMap;

  const priorityOrder = inspirationPriorityMap[currentType] || ["product", "person", "animal", "car"];

  let orderedInspirations: MagicBackgroundTemplate[] = [];

  priorityOrder.forEach((type) => {
    const typeInspirations = inspirations.value?.[type as MagicBackgroundForegroundType] || [];
    orderedInspirations = [...orderedInspirations, ...typeInspirations];
  });

  return orderedInspirations;
};

const scrollContainerHeight = computed(() => {
  return isMobile.value ? "18rem" : "23rem";
});
</script>

<style lang="scss" scoped>
.tile-grid-photos {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 0 0.25rem;

  @media (min-width: 376px) and (max-width: 767px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.divider {
  margin: 0 -16px !important;

  @media (min-width: 640px) {
    margin: 0 -24px !important;
  }
}

.scroll-container {
  overflow-x: hidden;
  overflow-y: auto;
  max-height: v-bind(scrollContainerHeight);
}

/* this feature is only supported on iOS, it's used here because scroll behavior is different on iOS and negative margin shifts the content */
@supports not (-webkit-touch-callout: none) {
  .scroll-container {
    margin-right: -1rem;
  }
}

.scroll-container::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 1rem;
}

.scroll-container::-webkit-scrollbar-thumb {
  border: 4px solid rgba(0, 0, 0, 0);
  background-clip: padding-box;
  -webkit-border-radius: 7px;
  background-color: rgba(0, 0, 0, 0.15);
  -webkit-box-shadow: inset -1px -1px 0px rgba(0, 0, 0, 0.05), inset 1px 1px 0px rgba(0, 0, 0, 0.05);
}
</style>
